<script setup>
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { htmlToText } from '~/common/utils/common.utils.js';

defineProps({
  activity: {
    type: Object,
    default: () => ({}),
  },
});

const { $t } = useCommonImports();
const WORKFLOW_ACTIVITY_VERB_MAP = {
  CREATED: () => `${$t('created the workflow')}`,
  DELETED: () => `${$t('deleted the workflow')}`,
};
</script>

<template>
  <div>
    <!-- For single static text -->
    <div v-if="Object.keys(WORKFLOW_ACTIVITY_VERB_MAP).includes(activity.verb)">
      {{ WORKFLOW_ACTIVITY_VERB_MAP[activity.verb](activity.meta) }}
    </div>
    <!-- For multiple static updated state texts -->
    <div v-else-if="activity.verb === 'UPDATED'">
      <div v-if="activity.verb === 'UPDATED'">
        <div v-if="activity.meta?.name">
          <span>
            {{ $t('renamed the workflow') }}
          </span>
          {{ $t('from') }}
          {{ activity.meta?.name?.from }}
          {{ $t('to') }}
          {{ activity.meta?.name?.to }}
        </div>
        <div v-if="activity.meta?.description">
          <span class="mr-1">
            {{ $t('updated the workflow description') }}
          </span>
          <span v-if="activity.meta?.description?.from">
            {{ $t('from') }}
            {{ htmlToText(activity.meta?.description.from) }}
          </span>
          <span>
            {{ $t('to') }}
            {{ htmlToText(activity.meta?.description?.to) }}
          </span>
        </div>
        <div v-if="activity.meta?.category">
          <span class="mr-1">
            {{ $t('updated the  workflow category') }}
          </span>
          <span
            v-if="activity.meta?.category?.from?.name"
            class="mr-1"
          >
            {{ $t('from') }}
            {{ activity.meta?.category?.from.name }}
          </span>
          <span>
            {{ $t('to') }}
            {{ activity.meta?.category?.to.name }}
          </span>
        </div>
        <div v-if="activity.meta?.active">
          <span v-if="activity.meta.active?.to === false">
            {{ $t('deactivated the workflow') }}
          </span>
          <span v-if="activity.meta.active?.to === true">
            {{ $t('reactivated the workflow') }}
          </span>
        </div>
        <div v-if="activity.meta?.members">
          <span class="mr-1">
            {{ $t('updated the workflow members') }}
          </span>

          <span v-if="activity.meta?.members?.from?.length" class="mr-1">
            {{ $t('from') }}
            <HawkMembers :members="activity.meta?.members?.from" type="badge" />
          </span>
          <span v-if="activity.meta?.members?.to">
            {{ $t('to') }}
            <HawkMembers :members="activity.meta?.members?.to" type="badge" />
          </span>
        </div>

        <div v-if="activity.meta?.submission_duration">
          <span class="mr-1">
            {{ $t('updated the workflow submission duration') }}
          </span>
          <span
            v-if="activity.meta?.submission_duration?.from"
            class="mr-1"
          >
            {{ $t('from') }}
            {{ activity.meta?.submission_duration?.from }}
          </span>
          <span>
            {{ $t('to') }}
            {{ activity.meta?.submission_duration?.to }}
          </span>
        </div>
        <div v-if="activity.meta?.resubmission_duration">
          <span class="mr-1">
            {{ $t('updated the workflow resubmission duration') }}
          </span>
          <span
            v-if="activity.meta?.resubmission_duration?.from"
            class="mr-1"
          >
            {{ $t('from') }}
            {{ activity.meta?.resubmission_duration?.from }}
          </span>
          <span>
            {{ $t('to') }}
            {{ activity.meta?.resubmission_duration?.to }}
          </span>
        </div>
        <div v-if="activity.meta?.submission_entry_status">
          <span class="mr-1">
            {{ $t('updated the workflow submission entry status') }}
          </span>
          <span
            v-if="activity.meta?.submission_entry_status?.from"
            class="mr-1"
          >
            {{ $t('from') }}
            {{ activity.meta?.submission_entry_status?.from }}
          </span>
          <span>
            {{ $t('to') }}
            {{ activity.meta?.submission_entry_status?.to }}
          </span>
        </div>
        <div v-if="activity.meta?.custom_field_mapping">
          {{ $t('updated the workflow field mapping') }}
        </div>
        <div v-if="activity.meta.workflow_data">
          <div v-if="activity.meta.workflow_data?.outcome_strategy">
            <span class="mr-1">
              {{ $t('updated the workflow outcome strategy') }}
            </span>
            <span
              v-if="activity.meta.workflow_data?.outcome_strategy?.from"
              class="mr-1"
            >
              {{ $t('from') }}
              {{ activity.meta.workflow_data?.outcome_strategy?.from }}
            </span>
            <span>
              {{ $t('to') }}
              {{ activity.meta.workflow_data?.outcome_strategy?.to }}
            </span>
          </div>
          <div v-if="activity.meta.workflow_data?.entry_status">
            <span class="mr-1">
              {{ $t('updated the workflow entry status') }}
            </span>
            <span
              v-if="activity.meta.workflow_data?.entry_status?.from"
              class="mr-1"
            >
              {{ $t('from') }}
              {{ activity.meta.workflow_data?.entry_status?.from }}
            </span>
            <span>
              {{ $t('to') }}
              {{ activity.meta.workflow_data?.entry_status?.to }}
            </span>
          </div>
          <div v-if="activity.meta.workflow_data?.status_map">
            <span>
              {{ $t('updated the workflow status map') }}
            </span>
          </div>
          <div v-if="activity.meta.workflow_data?.steps_updated || activity.meta.workflow_data?.steps_added || activity.meta.workflow_data?.steps_removed">
            {{ $t('updated the workflow steps') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
